/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action, computed } from '@ember/object';
import ObjectTypeModel from 'cio/models/object-type';
import { tracked } from '@glimmer/tracking';
import AlertsService from 'cio/services/alerts';
import { task } from 'ember-concurrency';
import DS from 'ember-data';
import RouterService from '@ember/routing/router-service';
import { singularize } from 'ember-inflector';
import FormValidations from 'cio/utils/form-validations';
import AnalyticsService from 'cio/services/analytics';
import { isEmpty } from '@ember/utils';
import EnvironmentService from 'cio/services/environment';
import { announceRefreshWorkspaces } from '@customerio/hydra-nav-state';
import HydraService from 'cio/services/hydra';
import ObjectTypesService from 'cio/services/object-types';
import FeaturesService from 'ember-feature-flags/services/features';
import AuthService from 'cio/services/auth';

interface NewObjectComponentArgs {
  model: ObjectTypeModel;
  objectTypes: ObjectTypeModel[];
}

type ObjectTypeFormValues = Pick<ObjectTypeModel, 'name' | 'singularName' | 'slug' | 'singularSlug' | 'icon'>;

const ICONS = [
  'object',
  'account-box',
  'archive',
  'billing-history',
  'calendar',
  'chart',
  'color-drop',
  'company',
  'contact',
  'copy-to-clipboard',
  'cursor',
  'delete-trash',
  'desktop',
  'draft',
  'editor-code',
  'editor-rich-text',
  'feature-launch',
  'file-csv',
  'file-pdf',
  'form',
  'function',
  'goal',
  'grid',
  'heart-pride',
  'icon',
  'image',
  'import',
  'in-app',
  'increasing',
  'integrations',
  'invoice',
  'javascript',
  'link',
  'list',
  'loading',
  'localization',
  'mobile',
  'newsletter',
  'send-auto',
  'setup-guide',
  'slack',
  'sms',
  'star',
  'stop',
  'swap',
  'table',
  'table-columns',
  'text-format',
  'time-delay',
  'time-window',
  'tip',
  'unarchive',
  'underline',
  'upload',
  'user-id',
  'variation',
  'webhook',
  'webhook-activity',
  'winner',
];

export default class ObjectTypeFormComponent extends Component<NewObjectComponentArgs> {
  @service auth!: AuthService;
  @service alerts!: AlertsService;
  @service store!: DS.Store;
  @service router!: RouterService;
  @service analytics!: AnalyticsService;
  @service environment!: EnvironmentService;
  @service hydra!: HydraService;
  @service('objectTypes') objectTypesService!: ObjectTypesService;
  @service features!: FeaturesService;

  readonly availableIcons = ICONS.map((icon) => ({
    label: `"${icon}"`,
    value: icon,
  }));

  @tracked businessType: string | null = null;
  @tracked objectTypeFormValues = {
    name: this.args.model.name,
    singularName: this.args.model.singularName,
    slug: this.args.model.slug,
    singularSlug: this.args.model.singularSlug,
    icon: this.args.model.icon || '',
  } as ObjectTypeFormValues;
  @tracked showSuccessModal = false;

  validations!: FormValidations;
  cheerImage = new URL('cio/assets/images/cheer.png', import.meta.url).toString();

  get isNewModel() {
    return this.args.model.get('isNew');
  }

  get otherObjectTypes() {
    let { id } = this.args.model;
    return this.args.objectTypes.filter((objectType) => objectType.id !== id);
  }

  get hasReadOnlyPermission() {
    if (this.isNewModel) {
      return !this.auth.hasPermission('journeys/object_types/create');
    }

    return !this.auth.hasPermission('journeys/object_types/edit');
  }

  private validateFieldInOtherTypes(field: 'name' | 'singularName' | 'slug' | 'singularSlug', value: string) {
    if (this.otherObjectTypes.some((objectType) => objectType[field] === value)) {
      let fieldName = field === 'singularName' ? 'singular name' : field;
      return `This "${fieldName}" is already in use for a different Object Type`;
    }
    return true;
  }

  validationsSettingsObject = {
    name: (value: string) => {
      if (value.trim().length === 0) {
        return 'Object name cannot be empty';
      }

      return this.validateFieldInOtherTypes('name', value);
    },
    singularName: (value: string) => {
      if (value.trim().length === 0) {
        return 'Object singular name cannot be empty';
      }

      return this.validateFieldInOtherTypes('singularName', value);
    },
    slug: (value: string) => {
      // \W matches all characters that are not alphanumeric or underscore
      if (/\W/.test(value?.trim())) {
        return 'Object slug allows only alphanumeric and underscore characters';
      }

      if (value?.trim().length === 0) {
        return 'Object slug cannot be empty';
      }

      return this.validateFieldInOtherTypes('slug', value);
    },
    singularSlug: (value: string) => {
      // \W matches all characters that are not alphanumeric or underscore
      if (/\W/.test(value?.trim())) {
        return 'Object singular slug allows only alphanumeric and underscore characters';
      }

      if (value?.trim().length === 0) {
        return 'Object singular slug cannot be empty';
      }

      return this.validateFieldInOtherTypes('singularSlug', value);
    },
    icon: (): true => {
      return true;
    },
  };

  constructor(owner: unknown, args: NewObjectComponentArgs) {
    super(owner, args);

    this.validations = FormValidations.create({
      validations: this.validationsSettingsObject,
      context: this,
      modelKey: 'objectTypeFormValues',
    });

    if (this.isNewModel && !isEmpty(this.args.model.name)) {
      this.validations.validateModel().catch(() => {});
    }

    this.analytics.track('objects', this.isNewModel ? 'object_type_create_started' : 'object_type_edit_started');
  }

  @computed('validations.hasErrors', 'objectTypesService.canConfigureObjectTypes')
  get saveDisabled(): boolean {
    return this.validations.hasErrors || !this.objectTypesService.canConfigureObjectTypes;
  }

  @computed('validations.errorsList.[]')
  get invalidErrorMessage(): string {
    return this.validations.errorsList.join(', and ');
  }

  get createText() {
    return `Create ${this.objectTypeFormValues.name || 'Object'}`;
  }

  @action
  onCloseSuccess() {
    this.showSuccessModal = false;
    this.router.transitionTo('workspace-settings.objects');
  }

  @action
  onObjectNameInput(value: string) {
    this.objectTypeFormValues = {
      name: value,
      singularName: singularize(value.trim()),
      slug: value.toLowerCase().trim().replace(/\s/g, '_'),
      singularSlug: singularize(value.trim()).toLowerCase().replace(/\s/g, '_'),
      icon: this.objectTypeFormValues.icon,
    };

    this.validations.validateModel().catch(() => {});
  }

  @action
  onIconChange(value: (typeof this.availableIcons)[number]) {
    this.onObjectTypeFormValueChange('icon', value.value);
  }

  @action
  onObjectTypeFormValueChange(field: keyof ObjectTypeFormValues, value: string) {
    let updatedValue = field === 'slug' ? value.replace(/\W/g, '') : value;
    this.objectTypeFormValues = {
      ...this.objectTypeFormValues,
      [field]: updatedValue,
    };
    this.validations.validateAttribute(field);
  }

  @action
  onReset() {
    let { name, singularName, slug, singularSlug, icon = '' } = this.args.model;
    if (this.isNewModel) {
      this.objectTypeFormValues = {
        name: '',
        singularName: '',
        slug: '',
        singularSlug: '',
        icon: '',
      };
    } else {
      this.objectTypeFormValues = {
        name,
        singularName,
        slug,
        singularSlug,
        icon,
      };
    }
    this.validations.reset();
  }

  @action
  setBusinessType(value: string) {
    this.businessType = value;
  }

  saveObjectType = task(async () => {
    let { model } = this.args;
    let { name } = this.objectTypeFormValues;
    let newModel = this.isNewModel;

    Object.keys(this.objectTypeFormValues).forEach((key: keyof ObjectTypeFormValues) => {
      model.set(key, this.objectTypeFormValues[key].trim());
    });

    try {
      await model.save();

      // In Ember Data 4.12 .peekAll directly after save is needed so further
      // ".peekAll('object-type').toArray()" calls (mostly "get objectTypes()" in multiple components)
      // don't error out with: "Expected to receive a stable Identifier to subscribe to"
      //
      // tests: http://127.0.0.1:4200/tests?testId=fe4075c6&testId=4b000751&testId=893d602d
      this.store.peekAll('object-type').toArray();

      if (this.businessType != null) {
        this.analytics.track('objects', 'business_type_self_selected', { businessType: this.businessType });
      }

      let msg = newModel ? `${name} custom object created` : `Changes to ${name} custom object saved`;
      this.alerts.success('object-type.success', msg);

      if (newModel) {
        this.showSuccessModal = true;
      } else {
        this.onCloseSuccess();
      }

      if (this.hydra.isHydraEnabled) {
        // let's force hydra to refresh the side navigation bar to add the new object
        announceRefreshWorkspaces();
      }
    } catch (e) {
      if (e?.errors?.[0]?.status === '403') {
        this.alerts.error(
          'object-type.error',
          `You do not have permission to ${
            newModel ? 'create' : 'edit'
          } this custom object. Please contact your Admin.`,
        );
        return;
      }

      if (e?.errors?.[0]?.status === '400' && e?.errors?.[0]?.detail.includes('limit reached') === true) {
        this.alerts.error(
          'object-type.error',
          `Your plan includes a maximum of ${this.environment.current.maxAllowedObjectTypes} object types. Disable or delete an existing type first.`,
        );
        return;
      }

      if (e?.errors?.[0]?.detail.includes('slug is being used') === true) {
        this.alerts.error('object-type.error', 'Cannot rename liquid tag because it is being used in liquid.');
        return;
      }

      this.alerts.error(
        'object-type.error',
        `Something went wrong when we tried to ${
          newModel ? 'create' : 'edit'
        } ${name} this custom object. Please try again.`,
      );
    }
  });
}
